import { Injectable } from '@angular/core';
import type { fhirR4 } from '@smile-cdr/fhirts';
import cloneDeep from 'lodash/cloneDeep';
import { PaNeededCode, PaNeededExtension } from '../models/dtr-types';

/**
 * Readers for the CRD `ext-coverage-information` extension:
 * https://hl7.org/fhir/us/davinci-crd/StructureDefinition-ext-coverage-information.html
 *
 * The extension is repeatable — an order carries one occurrence per
 * coverage/payer that responded — and each occurrence carries its own nested
 * extensions. Every accessor here therefore scans all occurrences rather than
 * assuming the payer of interest answered first.
 */
@Injectable({
  providedIn: 'root',
})
export class CoverageInformationService {
  static readonly coverageExtensionUrl =
    'http://hl7.org/fhir/us/davinci-crd/StructureDefinition/ext-coverage-information';
  static readonly paNeededExtensionUrl = 'pa-needed';
  static readonly authNeededValueCode: PaNeededCode = 'auth-needed';
  static readonly coverageAssertionIdExtensionUrl = 'coverage-assertion-id';
  static readonly pasClaimIdentifierExtensionUrl =
    'https://smiledigitalhealth.com/fhir/StructureDefinition/ext-pas-claim-identifier';

  /** Every `ext-coverage-information` occurrence on the order, in document order. */
  static getPAExtensions(
    order: fhirR4.Resource | undefined,
  ): fhirR4.Extension[] {
    return (
      order?.['extension']?.filter(
        (ext) => ext.url === CoverageInformationService.coverageExtensionUrl,
      ) ?? []
    );
  }

  /** Whether any payer has already recorded a coverage determination on the order. */
  static hasCoverageInformation(order: fhirR4.Resource | undefined): boolean {
    return CoverageInformationService.getPAExtensions(order).length > 0;
  }

  /** The DTR questionnaire the payer wants filled in, or `fallback` if none. */
  static getCanonicalUrl(
    order: fhirR4.Resource | undefined,
    fallback?: string,
  ): string | undefined {
    return (
      CoverageInformationService.findNestedExtension(order, 'questionnaire')
        ?.valueCanonical ?? fallback
    );
  }

  /** The payer's prior-auth determination for the order. */
  static getPANeeded(
    order: fhirR4.Resource | undefined,
  ): PaNeededCode | undefined {
    return (
      CoverageInformationService.findNestedExtension(
        order,
        CoverageInformationService.paNeededExtensionUrl,
      ) as PaNeededExtension | undefined
    )?.valueCode;
  }

  /**
   * The assertion id DTR echoes back as the `context` launch parameter.
   *
   * It is only sent for an occurrence whose `pa-needed` is `auth-needed` — that
   * payer is the one requiring prior auth, so its assertion is the one DTR is
   * launched against. Occurrences deciding `no-auth` or `conditional` are skipped
   * even when they carry an id. This gate is narrower than DTR
   * <a href="https://build.fhir.org/ig/HL7/davinci-dtr/en/confdetails.html#ci-c-spec-108">§spec-108</a>,
   * which keys off `info-needed`; EPAS-82 pinned it to `auth-needed` to match the
   * payer responses observed in test. Omitting the id when unavailable stays
   * consistent with
   * <a href="https://build.fhir.org/ig/HL7/davinci-dtr/en/confdetails.html#ci-c-spec-107">§spec-107</a>.
   *
   * The first occurrence that both requires auth and carries an id wins.
   */
  static getCoverageAssertionId(
    order: fhirR4.Resource | undefined,
  ): string | undefined {
    return CoverageInformationService.getPAExtensions(order)
      .filter(this.isAuthNeeded)
      .map((paExtension) => paExtension.extension ?? [])
      .map(
        (nested) =>
          nested.find(
            (ext) =>
              ext.url ===
              CoverageInformationService.coverageAssertionIdExtensionUrl,
          )?.valueString,
      )
      .find((assertionId) => !!assertionId);
  }

  /**
   * The identifier of the PAS claim already submitted for the order, recorded
   * when the PAS submit succeeded. Its presence means the order was submitted.
   */
  static getPasClaimIdentifier(
    order: fhirR4.Resource,
  ): fhirR4.Identifier | undefined {
    return CoverageInformationService.findNestedExtension(
      order,
      CoverageInformationService.pasClaimIdentifierExtensionUrl,
    )?.valueIdentifier;
  }

  /**
   * Returns a copy of `order` whose first `ext-coverage-information`
   * occurrence carries a sub-extension `{ url, [label]: value }`. A
   * sub-extension with the same `url` is replaced in place; otherwise the new
   * one is appended. The copy is returned unchanged when the order has no
   * `ext-coverage-information`.
   */
  static addOrReplaceNestedExtension<K extends keyof fhirR4.Extension>(
    order: fhirR4.Resource,
    url: string,
    label: K,
    value: fhirR4.Extension[K],
  ): fhirR4.Resource {
    const updated = cloneDeep(order);
    const target = CoverageInformationService.getPAExtensions(updated).at(0);
    if (!target) {
      return updated;
    }
    target.extension ??= [];
    const subExtension = { url, [label]: value } as fhirR4.Extension;
    const index = target.extension.findIndex((ext) => ext.url === url);
    if (index >= 0) {
      target.extension[index] = subExtension;
    } else {
      target.extension.push(subExtension);
    }
    return updated;
  }

  private static isAuthNeeded(
    paExtension: fhirR4.Extension,
  ): paExtension is fhirR4.Extension & { extension: fhirR4.Extension[] } {
    return (
      paExtension.extension?.some(
        (ext) =>
          ext.url === CoverageInformationService.paNeededExtensionUrl &&
          ext.valueCode === CoverageInformationService.authNeededValueCode,
      ) ?? false
    );
  }

  private static findNestedExtension(
    order: fhirR4.Resource | undefined,
    url: string,
  ): fhirR4.Extension | undefined {
    return CoverageInformationService.getPAExtensions(order)
      .flatMap((paExtension) => paExtension.extension ?? [])
      .find((ext) => ext.url === url);
  }
}
